import * as React from "react"

/**
 * Light/dark theme, persisted to localStorage. The tokens system uses `.dark`
 * on any parent to flip aliases; this hook adds/removes it on <html>.
 */

const STORAGE_KEY = "pyxis:theme"
export type Theme = "light" | "dark"

function readInitial(): Theme {
  if (typeof window === "undefined") return "light"
  const stored = window.localStorage.getItem(STORAGE_KEY)
  if (stored === "light" || stored === "dark") return stored
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light"
}

function applyTheme(theme: Theme): void {
  const root = document.documentElement
  if (theme === "dark") root.classList.add("dark")
  else root.classList.remove("dark")
}

export function useTheme(): { theme: Theme; setTheme: (t: Theme) => void } {
  const [theme, setThemeState] = React.useState<Theme>(readInitial)

  React.useEffect(() => {
    applyTheme(theme)
  }, [theme])

  const setTheme = React.useCallback((t: Theme) => {
    setThemeState(t)
    try {
      window.localStorage.setItem(STORAGE_KEY, t)
    } catch {
      // Storage unavailable (private mode) — theme survives current session only.
    }
  }, [])

  return { theme, setTheme }
}
